import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useAuthPrompt } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Calculator,
  DollarSign,
  Calendar,
  Check,
  AlertCircle,
  Plus,
  Trash2,
  Settings,
  TrendingUp,
  FileText,
  Loader2,
  Info,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

export default function QuarterlyTaxes() {
  const { isAuthenticated } = useAuth();
  const { showAuthPrompt } = useAuthPrompt();
  
  const [loading, setLoading] = useState(true);
  const [taxData, setTaxData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showSettingsForm, setShowSettingsForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    quarter: 'Q1',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    confirmation_number: '',
    notes: ''
  });
  const [stateTaxRate, setStateTaxRate] = useState(0);
  const [saving, setSaving] = useState(false);

  const fetchTaxData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/business/quarterly-taxes`, {
        params: { year: selectedYear },
        withCredentials: true
      });
      setTaxData(response.data);
      setStateTaxRate(response.data.settings?.state_tax_rate || 0);
    } catch (error) {
      console.error('Error fetching tax data:', error);
      toast.error('Failed to load tax data');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchTaxData();
  }, [fetchTaxData]);

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      showAuthPrompt();
      return;
    }

    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/business/quarterly-taxes/payments`, {
        year: selectedYear,
        quarter: paymentForm.quarter,
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        confirmation_number: paymentForm.confirmation_number,
        notes: paymentForm.notes
      }, { withCredentials: true });
      
      toast.success('Payment recorded successfully');
      setShowPaymentForm(false);
      setPaymentForm({
        quarter: 'Q1',
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        confirmation_number: '',
        notes: ''
      });
      fetchTaxData();
    } catch (error) {
      toast.error('Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!isAuthenticated) {
      showAuthPrompt();
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/business/quarterly-taxes/payments/${paymentId}`, {
        withCredentials: true
      });
      toast.success('Payment deleted');
      fetchTaxData();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const handleSaveSettings = async () => {
    if (!isAuthenticated) {
      showAuthPrompt();
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/business/quarterly-taxes/settings`, {
        state_tax_rate: parseFloat(stateTaxRate) || 0
      }, { withCredentials: true });
      
      toast.success('Settings saved');
      setShowSettingsForm(false);
      fetchTaxData();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { income_summary, tax_estimates, quarters, payments, totals } = taxData || {};
  const progressPercent = totals?.total_owed > 0 
    ? Math.min(100, (totals.total_paid / totals.total_owed) * 100)
    : 0;

  return (
    <div className="p-6 space-y-6" data-testid="quarterly-taxes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Quarterly Tax Estimator</h1>
          <p className="text-muted-foreground mt-1">
            Estimate and track your quarterly tax payments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-2 rounded-lg border bg-background text-sm"
            data-testid="year-selector"
          >
            {[2024, 2025, 2026, 2027].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettingsForm(!showSettingsForm)}
            data-testid="settings-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button
            onClick={() => setShowPaymentForm(!showPaymentForm)}
            data-testid="record-payment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Record Payment
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettingsForm && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Tax Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="state-tax-rate">State Tax Rate (%)</Label>
                <Input
                  id="state-tax-rate"
                  type="number"
                  step="0.1"
                  min="0"
                  max="15"
                  value={stateTaxRate}
                  onChange={(e) => setStateTaxRate(e.target.value)}
                  placeholder="e.g., 5.0"
                  data-testid="state-tax-rate-input"
                />
                <p className="text-xs text-muted-foreground">
                  Enter 0 if your state has no income tax (TX, FL, WA, etc.)
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveSettings} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Settings
              </Button>
              <Button variant="outline" onClick={() => setShowSettingsForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Form */}
      {showPaymentForm && (
        <Card className="border-emerald-500/20 bg-emerald-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-500" />
              Record Tax Payment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRecordPayment} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quarter">Quarter</Label>
                  <select
                    id="quarter"
                    value={paymentForm.quarter}
                    onChange={(e) => setPaymentForm({...paymentForm, quarter: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border bg-background"
                    data-testid="payment-quarter-select"
                  >
                    <option value="Q1">Q1 (Jan-Mar)</option>
                    <option value="Q2">Q2 (Apr-May)</option>
                    <option value="Q3">Q3 (Jun-Aug)</option>
                    <option value="Q4">Q4 (Sep-Dec)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                    placeholder="0.00"
                    data-testid="payment-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="payment_date">Payment Date</Label>
                  <Input
                    id="payment_date"
                    type="date"
                    value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                    data-testid="payment-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmation">Confirmation #</Label>
                  <Input
                    id="confirmation"
                    type="text"
                    value={paymentForm.confirmation_number}
                    onChange={(e) => setPaymentForm({...paymentForm, confirmation_number: e.target.value})}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Record Payment
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowPaymentForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net Profit (YTD)</p>
                <p className="text-2xl font-bold text-blue-500">
                  {formatCurrency(income_summary?.net_profit || 0)}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500/40" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Estimated Tax</p>
                <p className="text-2xl font-bold text-amber-500">
                  {formatCurrency(totals?.total_owed || 0)}
                </p>
              </div>
              <Calculator className="w-8 h-8 text-amber-500/40" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Paid</p>
                <p className="text-2xl font-bold text-emerald-500">
                  {formatCurrency(totals?.total_paid || 0)}
                </p>
              </div>
              <Check className="w-8 h-8 text-emerald-500/40" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Remaining</p>
                <p className="text-2xl font-bold text-red-500">
                  {formatCurrency(totals?.remaining || 0)}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500/40" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Payment Progress</span>
            <span className="text-sm text-muted-foreground">
              {formatCurrency(totals?.total_paid || 0)} of {formatCurrency(totals?.total_owed || 0)}
            </span>
          </div>
          <Progress value={progressPercent} className="h-3" />
          <p className="text-xs text-muted-foreground mt-1">
            {progressPercent.toFixed(0)}% of estimated taxes paid
          </p>
        </CardContent>
      </Card>

      <Tabs defaultValue="quarters" className="space-y-4">
        <TabsList>
          <TabsTrigger value="quarters">Quarterly Breakdown</TabsTrigger>
          <TabsTrigger value="details">Tax Details</TabsTrigger>
          <TabsTrigger value="history">Payment History</TabsTrigger>
        </TabsList>

        {/* Quarters Tab */}
        <TabsContent value="quarters">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {quarters?.map((q) => {
              const isPaid = q.paid >= q.estimated;
              const isOverdue = new Date(q.due_date) < new Date() && !isPaid;
              const quarterProgress = q.estimated > 0 ? (q.paid / q.estimated) * 100 : 0;
              
              return (
                <Card 
                  key={q.quarter}
                  className={`${isPaid ? 'border-emerald-500/30 bg-emerald-500/5' : isOverdue ? 'border-red-500/30 bg-red-500/5' : ''}`}
                  data-testid={`quarter-card-${q.quarter}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold">{q.quarter}</h3>
                          {isPaid && (
                            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30">
                              <Check className="w-3 h-3 mr-1" />
                              Paid
                            </Badge>
                          )}
                          {isOverdue && (
                            <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Overdue
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{q.period}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">{formatCurrency(q.estimated)}</p>
                        <p className="text-xs text-muted-foreground">Due {formatDate(q.due_date)}</p>
                      </div>
                    </div>
                    
                    <Progress value={Math.min(100, quarterProgress)} className="h-2 mb-2" />
                    
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Paid: {formatCurrency(q.paid)}</span>
                      <span className={q.remaining > 0 ? 'text-amber-500' : 'text-emerald-500'}>
                        {q.remaining > 0 ? `${formatCurrency(q.remaining)} remaining` : 'Complete'}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Tax Details Tab */}
        <TabsContent value="details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Income Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                  Income Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Gross Income</span>
                  <span className="font-medium text-emerald-500">
                    +{formatCurrency(income_summary?.total_income || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Business Expenses</span>
                  <span className="font-medium text-red-500">
                    -{formatCurrency(income_summary?.total_expenses || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Mileage Deduction</span>
                  <span className="font-medium text-red-500">
                    -{formatCurrency(income_summary?.mileage_deduction || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 text-lg font-semibold">
                  <span>Net Profit</span>
                  <span className="text-blue-500">
                    {formatCurrency(income_summary?.net_profit || 0)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Tax Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-500" />
                  Tax Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Self-Employment Tax (15.3%)</span>
                  <span className="font-medium">
                    {formatCurrency(tax_estimates?.self_employment_tax || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">SE Tax Deduction (50%)</span>
                  <span className="font-medium text-emerald-500">
                    -{formatCurrency(tax_estimates?.se_tax_deduction || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Federal Income Tax</span>
                  <span className="font-medium">
                    {formatCurrency(tax_estimates?.federal_income_tax || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">State Tax ({stateTaxRate}%)</span>
                  <span className="font-medium">
                    {formatCurrency(tax_estimates?.state_tax || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 text-lg font-semibold">
                  <span>Total Annual Tax</span>
                  <span className="text-amber-500">
                    {formatCurrency(tax_estimates?.total_annual_tax || 0)}
                  </span>
                </div>
                <div className="flex justify-between py-2 bg-primary/5 rounded-lg px-3 -mx-3">
                  <span className="font-medium">Quarterly Payment</span>
                  <span className="font-bold text-primary">
                    {formatCurrency(tax_estimates?.quarterly_payment || 0)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Box */}
          <Card className="mt-4 border-blue-500/20 bg-blue-500/5">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-500 mb-1">About Quarterly Taxes</p>
                  <p className="text-muted-foreground">
                    Self-employed individuals must pay estimated taxes quarterly. The IRS requires payments 
                    if you expect to owe $1,000 or more when you file. Pay at least 100% of last year's 
                    tax liability to avoid penalties (safe harbor rule).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Payment History</CardTitle>
              <CardDescription>
                All tax payments recorded for {selectedYear}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {payments && payments.length > 0 ? (
                <div className="space-y-2">
                  {payments.map((payment) => (
                    <div 
                      key={payment.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50"
                      data-testid={`payment-${payment.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                          <Check className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div>
                          <p className="font-medium">{payment.quarter} Payment</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(payment.payment_date)}
                            {payment.confirmation_number && ` • #${payment.confirmation_number}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-emerald-500">
                          {formatCurrency(payment.amount)}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-red-500"
                          onClick={() => handleDeletePayment(payment.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <DollarSign className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">No payments recorded yet</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowPaymentForm(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Record First Payment
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
